import { cloneElement, type ReactElement, type ReactNode } from 'react';
import { ArrowLeft } from 'lucide-react';
import { cn } from '../lib/utils';
import { StatusView } from './status-view';

const HOME_ACTION_CLASSNAME =
  'group inline-flex items-center gap-2 rounded-md border border-border px-4 py-2.5 text-sm font-medium transition-colors hover:border-[var(--l3-violet)] hover:bg-[var(--l3-violet-soft)] hover:text-[var(--l3-violet)]';

export interface NotFoundViewProps {
  /** Override the default "Page not found" heading. */
  title?: string;
  /** Override the default one-line description. */
  description?: string;
  /** Label inside the home button. Defaults to "Back to home". */
  homeLabel?: string;
  /**
   * Framework-specific home link — pass a bare element such as
   * `<Link to="/" />` (react-router-dom) or `<Link href="/" />` (next/link).
   * NotFoundView injects the styled className, the ArrowLeft icon, and the
   * `homeLabel` text as the child so callers don't duplicate chrome.
   */
  homeLink: ReactElement<{ className?: string; children?: ReactNode }>;
}

/**
 * Shared 404 page presentation used across L3 apps. Wraps <StatusView> with
 * preset copy and a styled "back home" button, while staying routing-agnostic
 * by slotting each app's own Link component.
 */
export function NotFoundView({
  title = 'Page not found',
  description = "The page you're looking for doesn't exist or has been moved.",
  homeLabel = 'Back to home',
  homeLink,
}: NotFoundViewProps) {
  const action = cloneElement(
    homeLink,
    { className: cn(HOME_ACTION_CLASSNAME, homeLink.props.className) },
    <>
      <ArrowLeft
        aria-hidden="true"
        className="size-4 transition-transform group-hover:-translate-x-0.5"
      />
      {homeLabel}
    </>,
  );
  return (
    <StatusView
      code="404"
      title={title}
      description={description}
      action={action}
    />
  );
}
